import { useMemo, useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { Box } from '@theme-ui/components';
import { useQuery } from '@apollo/client';

import { getLocalizationCountryCookie } from '@app/helpers/localization';
import { isNotNullish } from '@app/helpers/predicate';
import type { AddressCountriesQuery } from '@app/pages/Checkout/Address/AddressForm/__generated__/AddressCountriesQuery';
import { ADDRESS_COUNTRIES } from '@app/pages/Checkout/Address/AddressForm/AddressForm.queries';
import { sortCountries } from '@app/pages/Checkout/Address/helpers';

import StoreSwitchButton from './StoreSwitchButton';
import StoreSwitchPopover from './StoreSwitchPopover';

export type Option = {
  label?: string;
  value?: string;
};

const StoreSwitch = () => {
  const ref = useOnclickOutside(() => {
    setPopOverOpen(false);
  });

  const { data, loading: isLoading } = useQuery<AddressCountriesQuery>(
    ADDRESS_COUNTRIES,
    {
      fetchPolicy: 'cache-first',
      ssr: false,
    },
  );

  const currentCountryCode = getLocalizationCountryCookie();
  const countries = data?.magento2?.countries?.filter(isNotNullish);
  const sortedCountries = countries && sortCountries(countries);

  const options = useMemo(
    () =>
      sortedCountries?.map((country) => ({
        value: country?.id ?? undefined,
        label: country?.fullNameLocale ?? undefined,
      })),
    [sortedCountries],
  );

  const currentCountry = options?.find(
    (option) => option.value === currentCountryCode,
  );

  const [isPopOverOpen, setPopOverOpen] = useState(false);

  return (
    <Box ref={ref}>
      <StoreSwitchButton
        onClick={() => {
          setPopOverOpen(!isPopOverOpen);
        }}
        currentCountry={currentCountry}
      />

      {isPopOverOpen && (
        <StoreSwitchPopover
          isLoading={isLoading}
          currentCountry={currentCountry}
          options={options}
          close={() => setPopOverOpen(false)}
        />
      )}
    </Box>
  );
};

export default StoreSwitch;
